import React, { useState, useEffect } from "react";
import useStyles from "../../../../styles/search-room";
import { Typography, Container, Button } from "@material-ui/core";
import Navbar from "../../../../src/components/Navbar";
import Skeleton from "@material-ui/lab/Skeleton";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import nextDayDate from "../../../../src/utils/nextDayDate";
import { TFunction } from "next-i18next";
import { withTranslation, i18n, Router } from "../../../../i18n";
import { useRouter } from "next/router";
import { getHotelById } from "../../../../src/services/rooms";
import { HotelDetailDto } from "../../../../src/libs/hotel";
import dayjs from "dayjs";
import "dayjs/locale/id";
import ErrorPage from "../../../../src/components/ErrorPage";

type Props = {
  readonly t: TFunction;
};

interface DateCheckInType {
  in: Date | null;
  out: Date | null;
}

interface ErrorDto {
  error_code: string;
  errors: string[];
  message: string;
  status_code: number;
}

const Rooms = (props: Props) => {
  const router = useRouter();
  const { hotel_username } = router.query;
  const classes = useStyles(props);
  const { t } = props;
  const [dateCheckIn, setDateCheckIn] = useState<DateCheckInType>({
    in: new Date(),
    out: nextDayDate(new Date()),
  });
  const [openCheckIn, setOpenCheckIn] = useState(false);
  const [openCheckOut, setOpenCheckOut] = useState(false);
  const [hotelDetail, setHotelDetail] = useState<HotelDetailDto>(Object);
  const [isloading, setIsLoading] = useState(true);
  const [error, setError] = useState<ErrorDto>(Object);
  const language = i18n.language;

  const fetchHotelId = async () => {
    try {
      const res = await getHotelById(hotel_username);
      setHotelDetail(res.data);
      setIsLoading(false);
    } catch (error) {
      setError(error?.response?.data?.meta);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHotelId();
  }, []);

  const rangeNight = (first, end) => {
    const checkin = dayjs(first).format("YYYY-MM-DD");
    const checkout = dayjs(end).format("YYYY-MM-DD");
    return dayjs(checkout).diff(checkin, "days");
  };

  const handleSearch = () => {
    const checkin = dayjs(dateCheckIn.in).format("YYYY-MM-DD");
    const checkout = dayjs(dateCheckIn.out).format("YYYY-MM-DD");
    localStorage.setItem(
      "dateCheckIn",
      JSON.stringify({
        checkin: checkin,
        checkout: checkout,
      })
    );
    Router.push(
      `/hotels/${hotel_username}/rooms?checkin=${checkin}&checkout=${checkout}`
    );
  };

  function SkeleletonLoading() {
    return (
      <>
        <Skeleton variant="text" width={200} />
      </>
    );
  }

  return (
    <div>
      {error?.status_code || (!hotelDetail?.isActive && !isloading) ? (
        <ErrorPage
          error={error?.status_code || 400}
          errorMessage={
            error?.message || "Room Feature on this hotel was disabled!"
          }
          t={t}
        />
      ) : (
        <Container maxWidth="xs" className={classes.root}>
          <Navbar t={t} title="Room" withoutArrowBack />
          <div className={classes.wrapper}>
            <Typography className={classes.greeting1}>
              {t("choose-checkin-checkout?")}
            </Typography>

            <div className={classes.searchWrapper}>
              <div className={classes.areaSearch}>
                <div className={classes.nameHotel}>
                  <div style={{ marginRight: 10 }}>
                    <RoomOutlinedIcon color="primary" />
                  </div>
                  {isloading ? (
                    <SkeleletonLoading />
                  ) : (
                    <div>
                      <Typography className={classes.bold}>
                        {hotelDetail.name}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Typography variant="body2" className={classes.titleCard}>
                  Check-In
                </Typography>
                <div style={{ display: "flex", position: "relative" }}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                      value={dateCheckIn.in}
                      onChange={(date) =>
                        setDateCheckIn({
                          ...dateCheckIn,
                          in: date,
                          out: nextDayDate(date),
                        })
                      }
                      animateYearScrolling
                      minDate={dateCheckIn.in}
                      open={openCheckIn}
                      onClose={() => setOpenCheckIn(false)}
                      TextFieldComponent={() => (
                        <div
                          className={classes.areaSearch}
                          onClick={() => setOpenCheckIn(true)}
                        >
                          <div style={{ marginRight: 8 }}>
                            <img
                              src="/img/checkin-icon.svg"
                              className={classes.calendarIcon}
                              alt="checkin"
                            />
                          </div>
                          <div className={classes.searchLabel}>
                            <div>
                              <Typography className={classes.bold}>
                                {dayjs(dateCheckIn.in)
                                  .locale(language)
                                  .format("dddd, D MMMM YYYY")}
                              </Typography>
                            </div>
                          </div>
                        </div>
                      )}
                    />
                  </MuiPickersUtilsProvider>
                  <div className={classes.totalNight}>
                    <img
                      src="/img/moonlight.svg"
                      alt="moon"
                      style={{ marginRight: 4 }}
                    />
                    <Typography variant="caption">
                      {rangeNight(dateCheckIn.in, dateCheckIn.out)}
                    </Typography>
                    <div>
                      <Typography variant="caption">
                        {i18n.language === "en" &&
                        rangeNight(dateCheckIn.in, dateCheckIn.out) > 1
                          ? t("nights")
                          : t("night")}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <Typography variant="body2" className={classes.titleCard}>
                  Check-Out
                </Typography>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DatePicker
                    value={dateCheckIn.out}
                    onChange={(date) =>
                      setDateCheckIn({ ...dateCheckIn, out: date })
                    }
                    animateYearScrolling
                    open={openCheckOut}
                    minDate={nextDayDate(dateCheckIn.in)}
                    onClose={() => setOpenCheckOut(false)}
                    TextFieldComponent={() => (
                      <div
                        className={classes.areaSearch}
                        onClick={() => setOpenCheckOut(true)}
                      >
                        <div style={{ marginRight: 8 }}>
                          <img
                            src="/img/checkout-icon.svg"
                            className={classes.calendarIcon}
                            alt="checkout"
                          />
                        </div>
                        <div className={classes.searchLabel}>
                          <div>
                            <Typography className={classes.bold}>
                              {dayjs(dateCheckIn.out)
                                .locale(language)
                                .format("dddd, D MMMM YYYY")}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    )}
                  />
                </MuiPickersUtilsProvider>
              </div>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                className={classes.buttonSearch}
                onClick={handleSearch}
              >
                {t("find-rooms")}
              </Button>
            </div>
            <div className={classes.illustrationWrapper}>
              <img src="/img/hotel-book.svg" alt="hotel-book" />
            </div>
          </div>
        </Container>
      )}
    </div>
  );
};

Rooms.getInitialProps = async () => ({
  namespacesRequired: ["common"],
});

export default withTranslation("common")(Rooms);
